import { useMemo } from "react";
import { MapPin } from "lucide-react";
import { SectionCard, SectionHeader, KpiChip, ChartCard, MiniBar, SECTION_ACCENTS } from "./dashboardUi";

const C = SECTION_ACCENTS.installedBase.chart;

export default function InstalledBaseSection({ extra, delay }) {
  const ib = extra?.installed_base;
  const insights = useMemo(() => {
    if (!ib) return null;
    const regions = ib.regions || [];
    return {
      totalCount: ib.total_count || 0,
      totalKw: ib.total_kw || 0,
      districtsCovered: ib.districts_covered || 0,
      topRegion: regions[0]?.district || "—",
      barData: regions.slice(0, 8).map((r) => ({ month: r.district, value: r.install_count })),
      regions,
    };
  }, [ib]);

  if (!insights) return null;

  return (
    <SectionCard accent="installedBase" delay={delay} testid="dashboard-installed-base-section">
      <SectionHeader icon={MapPin} accent="installedBase" title="Installed Base · Regional Spread" subtitle="Where our active installations are — by district" />
      <div className="flex items-center gap-1 flex-wrap mb-3">
        <KpiChip label="Installed Systems" value={insights.totalCount} tone="bg-[#1F4E2C] text-white" to="/crm/installed-base" />
        <KpiChip label="Total Capacity" value={`${insights.totalKw} kW`} tone="bg-emerald-50 text-emerald-800" to="/crm/installed-base" />
        <KpiChip label="Districts Covered" value={insights.districtsCovered} to="/crm/installed-base" />
        <KpiChip label="Top District" value={insights.topRegion} to="/crm/installed-base" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <ChartCard title="Installs by District" testid="chart-ib-region-bar"><MiniBar data={insights.barData} color={C[0]} xKey="month" to="/crm/installed-base" /></ChartCard>
        <div className="border border-[#E2E4DC] bg-white p-3" data-testid="ib-region-table">
          <div className="font-mono text-[9.5px] uppercase tracking-[0.2em] text-[#4A5568] mb-2">District Breakdown</div>
          <div className="overflow-y-auto" style={{ maxHeight: 180 }}>
            <table className="w-full text-[11.5px]">
              <thead>
                <tr className="text-left font-mono text-[9px] uppercase tracking-widest text-[#4A5568] border-b border-[#EEF2EA]">
                  <th className="py-1.5">District</th>
                  <th className="py-1.5 text-right">Installs</th>
                  <th className="py-1.5 text-right">kW</th>
                  <th className="py-1.5 text-right">Active Tkts</th>
                </tr>
              </thead>
              <tbody>
                {insights.regions.map((r) => (
                  <tr key={r.district} className="border-b border-[#F4F6F1]" data-testid={`ib-region-row-${r.district}`}>
                    <td className="py-1.5 font-medium text-[#0F1A12]">{r.district}{r.state ? <span className="text-[#94A3B8]"> · {r.state}</span> : ""}</td>
                    <td className="py-1.5 text-right font-mono">{r.install_count}</td>
                    <td className="py-1.5 text-right font-mono">{r.total_kw}</td>
                    <td className="py-1.5 text-right font-mono text-red-600">{r.active_tickets}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </SectionCard>
  );
}
